import { Tooltip, type TooltipProps } from "@mui/material";
import { cloneElement, isValidElement, useState } from "react";

type Props = Omit<TooltipProps, "open" | "onOpen" | "onClose" | "disableTouchListener"> & {
  /** Ignore single taps; a double-tap opens the tooltip on touch devices. */
  doubleTapToOpen?: boolean;
};

/**
 * Tooltip that works on mobile with an explicit double-tap to open
 * (so a single tap still fires the underlying click / navigation).
 * On non-touch devices it behaves like a regular MUI Tooltip.
 */
export function TouchTooltip({ children, doubleTapToOpen = true, ...rest }: Props) {
  const [open, setOpen] = useState(false);
  const [lastTap, setLastTap] = useState(0);

  if (!doubleTapToOpen) {
    return <Tooltip {...rest}>{children}</Tooltip>;
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const now = Date.now();
    if (now - lastTap < 300) {
      e.preventDefault();
      setOpen(true);
      setTimeout(() => setOpen(false), 2000);
      setLastTap(0);
    } else {
      setLastTap(now);
    }
  };

  const wrapped = isValidElement(children)
    ? cloneElement(children as React.ReactElement<{
        onTouchEnd?: (e: React.TouchEvent) => void;
      }>, {
        onTouchEnd: handleTouchEnd
      })
    : children;

  return (
    <Tooltip
      {...rest}
      open={open || undefined}
      onClose={() => setOpen(false)}
      disableTouchListener
    >
      {wrapped}
    </Tooltip>
  );
}
